'use client';
import SafeLink from '@/components/SafeLink';
import HEADER_PAGE_TYPE from '@/constants/header';
import { useTranslation } from '@/i18n/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './style.module.scss';
import { spmTrackingUtils } from '@/intl-jdi-common-ssr/utils/spmTrackUtils';

import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/32399';
import {
  HOME_EVENT_NAMES,
  homeBusinessLogger,
  HOME_TRACK_TYPE,
} from '@/utils/logger/instance/homeBusinessLogger';
interface miniHomeProps {
  List: any;
  moduleData: any;
  pageCode: string;
  pathname: any;
  miniHomeTop: number;
  isScrollingUp: boolean;
  pageType: keyof typeof HEADER_PAGE_TYPE;
  miniHomeRef?: any;
  setCurrentTab?: any;
}

// ---------------- 小首页链接监控告警 ---------------- //
function checkHomeListLinks(homeList: any[]) {
  const hrefSet = new Set<string>();
  const currentHost = typeof window !== 'undefined' ? window.location.host : '';

  homeList.forEach((item, idx) => {
    let href = '';
    if (item.jumpLink?.jumpType === 'mini-home') {
      href = `/minihome/${item.jumpLink.jumpValue.pageCodeInfo?.pageCode}`;
    } else if (item.jumpLink?.jumpType === 'custom') {
      href = item.jumpLink?.jumpValue.link ?? '';
      try {
        const linkHost = href ? new URL(href, window.location.origin).host : '';
        if (linkHost && linkHost !== currentHost) {
          homeBusinessLogger({
            eventName: HOME_EVENT_NAMES.DOMAIN_DOESNT_MATCH,
            category: { type: HOME_TRACK_TYPE.CMS_CLICK_ERROR },
            message: {
              msg: '小首页域名不匹配',
              tabName: item.tabName || '-100',
              href: href || '-100',
              index: idx,
            },
          });
        }
      } catch (e) {
        homeBusinessLogger({
          eventName: HOME_EVENT_NAMES.LINK_PARSING_ERROR,
          category: { type: HOME_TRACK_TYPE.CMS_CLICK_ERROR },
          message: {
            msg: '小首页链接解析异常',
            tabName: item.tabName || '-100',
            href: href || '-100',
            index: idx,
          },
        });
      }
    }
    if (href) {
      if (hrefSet.has(href)) {
        homeBusinessLogger({
          eventName: HOME_EVENT_NAMES.MINIHOME_DUPLICATE_LINK,
          category: { type: HOME_TRACK_TYPE.CMS_CLICK_ERROR },
          message: {
            msg: '小首页tab链接重复',
            tabName: item.tabName || '-100',
            href: href || '-100',
            index: idx,
          },
        });
      } else {
        hrefSet.add(href);
      }
    }
  });
}

export default function MiniHome({
  List,
  pageCode,
  moduleData,
  pathname,
  miniHomeTop,
  isScrollingUp,
  pageType,
  miniHomeRef,
  setCurrentTab,
}: miniHomeProps) {
  const { t } = useTranslation();
  const tabHome = {
    tabName: t('minihome.home'),
    highLight: '',
    jumpLink: {
      jumpType: 'custom',
      jumpValue: {
        link: '/',
      },
    },
  };
  let homeList = [];

  homeList = [tabHome, ...List];

  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isHome = pathname === '/' || pathname === '/cms/home';
  // ---------------- 吸顶样式 ---------------- //
  const [stickyStyle, setStickyStyle] = useState<any>({});

  useEffect(() => {
    const updateStickyStyle = () => {
      setStickyStyle({
        position: 'sticky',
        top: `${miniHomeTop}px`,
        zIndex: 1000,
      });
    };
    // 初始设置
    updateStickyStyle();
  }, [miniHomeTop]);

  // ---------------- 确定选中tab的Index ---------------- //
  const getActiveIndex = useCallback(() => {
    const index = homeList.findIndex(
      (item: any) => pathname === `/minihome/${item?.jumpLink?.jumpValue?.pageCodeInfo?.pageCode}`,
    );
    return index !== -1 ? index : 0; // 如果没找到，默认返回 0
  }, [homeList, pathname]);
  const index = getActiveIndex();
  const [homeActive, setHomeActive] = useState(index);

  // ---------------- 选中tab居中 ---------------- //
  const scrollToCenter = (index: number) => {
    const container = containerRef.current;
    const targetTab = tabRefs.current[index];

    if (!container || !targetTab) return;

    const containerWidth = container.offsetWidth;
    const targetTabLeft = targetTab.offsetLeft;
    const targetTabWidth = targetTab.offsetWidth;

    const targetTabCenter = targetTabLeft + targetTabWidth / 2;
    const containerCenter = containerWidth / 2;

    // 需要的滚动距离
    let scrollLeft = targetTabCenter - containerCenter;

    // 限制滚动范围，避免超出边界
    const maxScrollLeft = container.scrollWidth - containerWidth;
    scrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));

    // 检查是否需要居中（头尾距离是否足够）
    const canCenterLeft = targetTabCenter >= containerCenter;
    const canCenterRight = targetTabCenter <= container.scrollWidth - containerCenter;

    if (canCenterLeft && canCenterRight) {
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    } else if (!canCenterLeft) {
      // 靠近左边界，滚动到最左边
      container.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else if (!canCenterRight) {
      // 靠近右边界，滚动到最右边
      container.scrollTo({
        left: maxScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const activeIndex = getActiveIndex();
    scrollToCenter(activeIndex);
    setHomeActive(activeIndex);
  }, [getActiveIndex]);

  // ---------------- 样式设置 ---------------- //
  const activeFontStyle = {
    color: '#1a1a1a',
    fontWeight: 700,
  };
  const activeRectStyle = {
    visibility: 'visible' as const,
    backgroundColor: '#1a1a1a',
  };

  // ---------------- tab自适应/平分逻辑 ---------------- //
  const [isFullWidth, setIsFullWidth] = useState(true);

  useEffect(() => {
    if (!containerRef.current || !tabRefs.current.length) return;
    // 计算所有tab宽度总和
    const tabSumWidth = tabRefs.current.reduce((acc, el) => acc + (el?.offsetWidth || 0), 0);
    const containerWidth = containerRef.current.offsetWidth;
    // 平分：tab总宽小于容器宽
    setIsFullWidth(tabSumWidth <= containerWidth);
  }, [homeList]);

  if (homeList.length === 0) {
    if (List?.length === 0) {
      homeBusinessLogger({
        eventName: HOME_EVENT_NAMES.MINIHOME_NO_DATA,
        category: { type: HOME_TRACK_TYPE.CMS_JS_ERROR },
        message: {
          msg: '小首页数据为空，不展示小首页',
        },
      });
      return null;
    }
    return null;
  }
  checkHomeListLinks(homeList);
  return (
    <div
      id='miniHome'
      className={styles['uk-miniHome']}
      ref={miniHomeRef}
      style={isHome ? (!isScrollingUp ? {} : stickyStyle) : stickyStyle}
    >
      <div
        className={styles.homeContainer + ' ' + (isFullWidth ? styles.fullWidth : '')}
        ref={containerRef}
      >
        {homeList.map((item: any, index: number) => (
          <div
            id={`${item?.jumpLink?.jumpValue?.pageCodeInfo?.pageCode}_${index + 1}`}
            className={styles.tab + ' ' + (isFullWidth ? styles.fullWidthTab : '')}
            style={
              homeActive === index
                ? activeFontStyle
                : item.highLight
                  ? { color: item.highLight }
                  : {}
            }
            key={index}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
            data-exp={JSON.stringify({
              spm: `a0145.b002218.c00002476.${index + 1}`,
              event_id: 'Home_Mini_Hp_Tab_Expo',
              page_id: 'Home_Main',
              json_param: JSON.stringify({
                index: index,
                Mini_Hp_Name: item.jumpLink?.jumpValue.pageCodeInfo?.pageName || 'home',
              }),
            })}
            onClick={(e) => {
              setCurrentTab(item?.jumpLink?.jumpValue?.pageCodeInfo?.pageName);
              spmTrackingUtils.spmClick({
                spm: `a0145.b002218.c00002476.${index + 1}`,
                event_id: 'Home_Mini_Hp_Tab',
                page_id: 'Home_Main',
                json_param: JSON.stringify({
                  index: index,
                  Mini_Hp_Name: item.jumpLink?.jumpValue.pageCodeInfo?.pageName || 'home',
                }),
                target: e.target as HTMLElement,
              });
            }}
          >
            <SafeLink
              href={
                item?.jumpLink?.jumpType === 'mini-home'
                  ? `/minihome/${item?.jumpLink?.jumpValue?.pageCodeInfo?.pageCode}`
                  : item?.jumpLink?.jumpType === 'custom'
                    ? item?.jumpLink?.jumpValue?.link
                    : {}
              }
            >
              {item.tabName}{' '}
            </SafeLink>
            <div
              className={styles.rect}
              style={homeActive === index ? activeRectStyle : { visibility: 'hidden' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
